import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { TrendingUp, Users, Flame, AlertCircle, Target as TargetIcon, CheckCircle2, Snowflake, MessageCircle, Clock, HelpCircle } from "lucide-react";

const STATUS_COLORS = {
  Hot: "#FF3B30",
  Warm: "#FF9F0A",
  Cold: "#0A84FF",
  "Followup 1": "#32D74B",
};

function sumKey(arr, key) {
  if (!arr || arr.length === 0) return "—";
  return arr.reduce((acc, x) => acc + (x[key] || 0), 0);
}

function Metric({ label, value, icon: Icon, accent = "primary", testId }) {
  const colorMap = {
    primary: "text-primary",
    destructive: "text-destructive",
    warning: "text-[#FF9F0A]",
    success: "text-[#32D74B]",
  };
  return (
    <Card data-testid={testId} className="bg-[#121212] border-border p-5 rounded-md">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">{label}</div>
          <div className="font-mono text-3xl font-bold text-white mt-3 tracking-tight">{value}</div>
        </div>
        <Icon className={`w-5 h-5 ${colorMap[accent]}`} strokeWidth={1.5} />
      </div>
    </Card>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [overview, setOverview] = useState(null);
  const [personal, setPersonal] = useState(null);
  const [coldPipeline, setColdPipeline] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        if (isAdmin) {
          const [m, c] = await Promise.all([
            api.get("/analytics/me"),
            api.get("/analytics/cold-pipeline", { params: { days: 3, limit: 10 } }),
          ]);
          setPersonal(m.data);
          setColdPipeline(c.data);
        } else {
          const [o, p, c] = await Promise.all([
            api.get("/analytics/overview"),
            api.get("/analytics/me"),
            api.get("/analytics/cold-pipeline", { params: { days: 3, limit: 10 } }),
          ]);
          setOverview(o.data);
          setPersonal(p.data);
          setColdPipeline(c.data);
        }
      } catch (e) {
        console.error(e);
      }
    })();
  }, [isAdmin]);

  if (isAdmin) {
    return (
      <div className="space-y-6" data-testid="admin-dashboard">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">My Console</div>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight mt-1">
            Today's mission, <span className="text-primary">{user?.name?.split(" ")[0]}</span>.
          </h1>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Metric testId="metric-assigned" label="Assigned to me" value={personal?.total_assigned ?? "—"} icon={Users} />
          <Metric testId="metric-untouched" label="Untouched" value={personal?.untouched ?? "—"} icon={AlertCircle} accent="destructive" />
          <Metric testId="metric-hot" label="Hot leads" value={personal?.hot ?? "—"} icon={Flame} accent="destructive" />
          <Metric testId="metric-today" label="Touched today" value={personal?.today_touched ?? "—"} icon={CheckCircle2} accent="success" />
        </div>

        {/* Hasil Akhir KPI */}
        <div data-testid="hasil-akhir-kpi-personal">
          <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-3">Hasil akhir saya</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Metric testId="metric-selesai" label="Selesai (Terproses)" value={personal?.selesai ?? "—"} icon={CheckCircle2} accent="success" />
            <Metric testId="metric-progress" label="Progress (On Proses)" value={personal?.progress ?? "—"} icon={Clock} accent="warning" />
            <Metric testId="metric-belum" label="Belum disentuh" value={personal?.belum ?? "—"} icon={HelpCircle} />
          </div>
        </div>

        <Card className="bg-[#121212] border-border p-6 rounded-md">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Daily target</div>
              <div className="font-heading text-xl text-white font-semibold mt-1">
                {personal?.today_touched ?? 0} / {personal?.daily_target ?? 20} follow-ups
              </div>
            </div>
            <TargetIcon className="w-6 h-6 text-primary" strokeWidth={1.5} />
          </div>
          <Progress
            value={Math.min(100, ((personal?.today_touched ?? 0) / (personal?.daily_target ?? 20)) * 100)}
            className="h-2"
          />
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatusPie data={[
            { name: "Hot", value: personal?.hot ?? 0 },
            { name: "Warm", value: personal?.warm ?? 0 },
            { name: "Cold", value: personal?.cold ?? 0 },
          ]} />
          <div className="md:col-span-2">
            <ColdPipeline data={coldPipeline} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="overview-dashboard">
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">Overview</div>
        <h1 className="font-heading text-3xl font-bold text-white tracking-tight mt-1">
          Performance command center.
        </h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Metric testId="metric-total" label="Total leads" value={overview?.total_leads ?? "—"} icon={Users} />
        <Metric testId="metric-unassigned" label="Unassigned" value={overview?.unassigned ?? "—"} icon={AlertCircle} accent="destructive" />
        <Metric testId="metric-stages" label="Active stages" value={overview?.per_stage?.length ?? "—"} icon={TrendingUp} />
        <Metric testId="metric-admins" label="Active admins" value={overview?.admin_performance?.length ?? "—"} icon={Flame} accent="warning" />
      </div>

      {/* Global Hasil Akhir totals (aggregated across all admins) */}
      <div data-testid="hasil-akhir-kpi-global">
        <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-3">Hasil akhir agregat</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Metric testId="metric-global-selesai" label="Selesai (Terproses)" value={sumKey(overview?.admin_performance, "selesai")} icon={CheckCircle2} accent="success" />
          <Metric testId="metric-global-progress" label="Progress (On Proses)" value={sumKey(overview?.admin_performance, "progress")} icon={Clock} accent="warning" />
          <Metric testId="metric-global-belum" label="Belum disentuh" value={sumKey(overview?.admin_performance, "belum")} icon={HelpCircle} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="bg-[#121212] border-border p-6 rounded-md">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Leads per stage</div>
              <div className="font-heading text-lg text-white font-semibold mt-1">Pipeline distribution</div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={overview?.per_stage || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
              <XAxis dataKey="name" stroke="#8E8E93" fontSize={11} />
              <YAxis stroke="#8E8E93" fontSize={11} />
              <Tooltip
                contentStyle={{ background: "#0A0A0A", border: "1px solid #262626", borderRadius: 8 }}
                labelStyle={{ color: "#fff" }}
              />
              <Bar dataKey="count" fill="#007AFF" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <StatusPie data={(overview?.status_distribution || []).map((s) => ({ name: s.status, value: s.count }))} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card data-testid="admin-performance-card" className="bg-[#121212] border-border p-6 rounded-md">
          <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-4">
            Admin performance · Hasil akhir
          </div>
          <div className="space-y-3">
            {(overview?.admin_performance || []).map((a) => {
              const total = a.total || 0;
              const sel = a.selesai || 0;
              const prog = a.progress || 0;
              const bel = a.belum || 0;
              const pct = (n) => (total > 0 ? Math.round((n / total) * 100) : 0);
              return (
                <div
                  key={a.admin_id}
                  data-testid={`admin-perf-${a.admin_id}`}
                  className="p-3 border border-border rounded-md hover:bg-[#1C1C1E] transition-colors"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="text-sm text-white font-medium">{a.name}</div>
                      <div className="text-[10px] font-mono text-muted-foreground tracking-wider mt-0.5">
                        {a.hot} HOT · {total} TOTAL
                      </div>
                    </div>
                    <div className="font-mono text-2xl font-bold text-primary">{total}</div>
                  </div>
                  {/* Hasil akhir buckets */}
                  <div className="grid grid-cols-3 gap-2 text-[11px]">
                    <div className="text-center p-1.5 rounded bg-[#32D74B]/10 border border-[#32D74B]/30">
                      <div className="font-mono text-[#32D74B] font-semibold">{sel}</div>
                      <div className="text-[9px] tracking-wider uppercase text-muted-foreground mt-0.5">Selesai</div>
                    </div>
                    <div className="text-center p-1.5 rounded bg-[#FF9F0A]/10 border border-[#FF9F0A]/30">
                      <div className="font-mono text-[#FF9F0A] font-semibold">{prog}</div>
                      <div className="text-[9px] tracking-wider uppercase text-muted-foreground mt-0.5">Progress</div>
                    </div>
                    <div className="text-center p-1.5 rounded bg-[#0A84FF]/10 border border-[#0A84FF]/30">
                      <div className="font-mono text-[#0A84FF] font-semibold">{bel}</div>
                      <div className="text-[9px] tracking-wider uppercase text-muted-foreground mt-0.5">Belum</div>
                    </div>
                  </div>
                  {/* Conversion rate */}
                  {total > 0 && (
                    <div className="mt-2 flex items-center justify-between text-[10px] font-mono text-muted-foreground">
                      <span>Konversi</span>
                      <span className="text-[#32D74B] font-semibold">{pct(sel)}%</span>
                    </div>
                  )}
                </div>
              );
            })}
            {(!overview?.admin_performance || overview.admin_performance.length === 0) && (
              <div className="text-sm text-muted-foreground text-center py-6">No admin data yet.</div>
            )}
          </div>
        </Card>

        <ColdPipeline data={coldPipeline} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="bg-[#121212] border-border p-6 rounded-md">
          <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-4">
            Top sources
          </div>
          <div className="space-y-3">
            {(overview?.top_sources || []).map((s, idx) => (
              <div key={s.source} className="flex items-center gap-4">
                <div className="font-mono text-xs text-muted-foreground w-6">#{idx + 1}</div>
                <div className="flex-1">
                  <div className="flex justify-between mb-1.5">
                    <div className="text-sm text-white">{s.source}</div>
                    <div className="font-mono text-xs text-primary">{s.count}</div>
                  </div>
                  <div className="h-1.5 bg-[#1C1C1E] rounded-sm overflow-hidden">
                    <div
                      className="h-full bg-primary"
                      style={{ width: `${(s.count / (overview.top_sources[0]?.count || 1)) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
            {(!overview?.top_sources || overview.top_sources.length === 0) && (
              <div className="text-sm text-muted-foreground text-center py-6">No source data yet.</div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

function StatusPie({ data }) {
  const filtered = (data || []).filter((d) => d.value > 0);
  return (
    <Card className="bg-[#121212] border-border p-6 rounded-md">
      <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-4">
        Status distribution
      </div>
      {filtered.length === 0 ? (
        <div className="h-[240px] flex items-center justify-center text-muted-foreground text-sm">
          No status data yet.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={240}>
          <PieChart>
            <Pie data={filtered} dataKey="value" nameKey="name" innerRadius={60} outerRadius={90} paddingAngle={3}>
              {filtered.map((entry) => (
                <Cell key={entry.name} fill={STATUS_COLORS[entry.name] || "#8E8E93"} stroke="#0A0A0A" strokeWidth={2} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{ background: "#0A0A0A", border: "1px solid #262626", borderRadius: 8 }}
            />
            <Legend wrapperStyle={{ fontSize: 11, color: "#8E8E93" }} />
          </PieChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}


function openWhatsAppFromCold(item) {
  const phone = (item.no_wa || "").replace(/\D/g, "");
  if (!phone) return;
  const text = encodeURIComponent(
    `Halo ${item.nama || "calon mahasiswa"}, semoga sehat selalu. Kami ingin menindaklanjuti pendaftaran Anda. Apakah ada yang bisa kami bantu?`
  );
  window.open(`https://wa.me/${phone}?text=${text}`, "_blank");
}

function daysSince(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86400000));
}

function ColdPipeline({ data }) {
  const items = data?.items || [];
  return (
    <Card data-testid="cold-pipeline-widget" className="bg-[#121212] border-border p-6 rounded-md">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground flex items-center gap-2">
            <Snowflake className="w-3.5 h-3.5 text-[#0A84FF]" strokeWidth={1.5} />
            Cold pipeline alert
          </div>
          <div className="font-heading text-lg text-white font-semibold mt-1">
            {data?.total ?? 0} lead butuh follow-up
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            Belum dihubungi {data?.days ?? 3} hari terakhir
          </div>
        </div>
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-muted-foreground text-center py-8 border border-dashed border-border rounded-md">
          Semua lead terkini · tidak ada yang dingin 🎯
        </div>
      ) : (
        <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
          {items.map((it) => {
            const d = daysSince(it.last_contact);
            return (
              <div
                key={it.id}
                data-testid={`cold-item-${it.id}`}
                className="flex items-center justify-between gap-3 p-3 border border-border rounded-md hover:bg-[#1C1C1E] transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white font-medium truncate">
                    {it.nama || "(tanpa nama)"}
                  </div>
                  <div className="text-[10px] font-mono text-muted-foreground tracking-wider mt-1 flex items-center gap-2 flex-wrap">
                    {it.status && <span className="px-1.5 py-0.5 rounded bg-[#1C1C1E] border border-border">{it.status}</span>}
                    <span>{d == null ? "Belum kontak" : `${d}h tanpa kontak`}</span>
                    {it.admin_name && <span className="text-primary">{it.admin_name}</span>}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openWhatsAppFromCold(it)}
                  disabled={!it.no_wa}
                  className="border-[#32D74B]/30 bg-[#32D74B]/10 text-[#32D74B] hover:bg-[#32D74B]/20 h-7 px-2.5"
                >
                  <MessageCircle className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} />
                  WA
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
